(ns machtfit.product-form.promotion-image
  (:require [clojure.set :as set]
            [machtfit.product-form.core :as product-form]
            [machtfit.product-form.fileupload :as fileupload]
            [machtfit.utils :refer [compare-by in?]]
            [re-frame.core :as rf]))

(defn- image-usage-text [usages]
  (case usages
    0 "Das Bild wird noch nicht genutzt"
    1 "1 Angebot nutzt dieses Bild"
    (str usages " Angebote nutzen dieses Bild")))

(rf/reg-sub ::image-by-id
  :<- [:get [:images]]

  (fn [images [_ current-image-id]]
    (when (some? current-image-id)
      (first (filter #(= current-image-id (:id %)) images)))))

(rf/reg-sub ::current-image
  :<- [:get [:images]]
  :<- [:get [:current-image-id]]

  (fn [[images current-image-id] _]
    (when (some? current-image-id)
      (first (filter #(= current-image-id (:id %)) images)))))

(rf/reg-sub ::image-categories
  :<- [:get [:categories]]
  :<- [:get [:images]]

  (fn [[product-categories images] _]
    (->> images
         (map :categories)
         seq
         flatten
         (apply sorted-set)
         (set/union product-categories))))

(rf/reg-sub ::images-in-category?
  (fn [[_ category]]
    [(rf/subscribe [::images-in-category category])])

  (fn [[images-in-category'] _]
    (not (empty? images-in-category'))))

(defn- images-in-category [images current-category current-image category]
  (let [is-current-category? (= current-category category)
        base-set (set (filter #(in? (:categories %) category) images))
        add-current-image? (and is-current-category? (some? current-image) (not (contains? base-set current-image)))]
    (cond-> base-set
      add-current-image? (conj current-image))))

(rf/reg-sub ::images-in-category
  :<- [:get [:images]]
  :<- [:get [:product :what :category]]
  :<- [::current-image]

  (fn [[images current-category current-image] [_ category]]
    (images-in-category images current-category current-image category)))

(rf/reg-sub ::partner-images
  :<- [:get [:images]]
  :<- [::current-image]

  (fn [[images current-image] _]
    (let [base-set (filter #(= (:source %) :partner) images)
          add-current-image? (and (= (:source current-image) :partner) (every? #(not= (:id current-image) (:id %)) base-set))]
      (cond-> base-set
        add-current-image? (conj current-image)))))

(rf/reg-sub ::partner-images?
  :<- [::partner-images]

  (fn [partner-images _]
    (not (empty? partner-images))))

(defn- select-image [current-db]
  (-> current-db
      (assoc-in [:ui :promotion-image-media-library-active?] false)
      (fileupload/reset-error :promotion-image)
      (assoc-in [:ui :image-set-manually?] true)))

(rf/reg-event-fx ::select-image
  (fn [{:keys [db]} [_ promotion-index image-id]]
    {:db (select-image db)
     :dispatch [::product-form/set-promotion-attribute promotion-index :image-id image-id]}))

(rf/reg-event-fx ::add-and-select-image
  (fn [{:keys [db]} [_ promotion-index image]]
    (let [images (:images db)
          already-exists? (some #(= (:id image) (:id %)) images)]
      {:db (-> db
               (assoc :images (cond-> images (not already-exists?) (conj image)))
               (select-image))
       :dispatch [::product-form/set-promotion-attribute promotion-index :image-id (:id image)]})))

(rf/reg-event-db ::toggle-promotion-image-library-active
  (fn [db [_ image-id]]
    (-> db
        (fileupload/reset-error :promotion-image)
        (update-in [:ui :promotion-image-media-library-active?] not)
        (assoc :current-image-id image-id))))

(defn image [promotion-index {:keys [image-id finished?]} show-action]
  (let [current-image @(rf/subscribe [::image-by-id image-id])
        image-url (:url current-image)
        on-click (when show-action #(rf/dispatch [::toggle-promotion-image-library-active image-id]))
        on-click-remove (when show-action #(rf/dispatch [::product-form/set-promotion-attribute promotion-index :image-id nil]))]
    [:div.card.center
     [:div.card-image {:style {:padding "0.5rem"}}
      (if (some? image-url)
        [:img {:src image-url
               :on-click on-click
               :style (when show-action {:cursor "pointer"})}]
        [:div {:style {:color "grey"}}
         [:div
          {:on-click (when show-action on-click)
           :style {:font-size "100px" :cursor "pointer"}}
          [:i.icon-picture]]
         [:div
          [:em "kein Bild"]]])
      (when-not finished?
        [:div.card-action
         (when (not show-action) {:style {:opacity 0}})
         (if image-url
           [:button.waves-effect.waves-light.btn.red
            {:on-click on-click-remove}
            "Bild löschen"]
           [:button.waves-effect.waves-light.btn
            {:on-click on-click}
            "Bild auswählen"])])]]))

(def ^:private thumbnail-size {:width "151px" :height "121px"})

(defn image-library-upload [promotion-index]
  (fileupload/fileupload {:id "image"
                          :input-element-id "image-upload-input"
                          :display-name "image-library-upload"
                          :param-name "image"
                          :label "Bild hochladen"
                          :accept "image/*"
                          :upload-url @(rf/subscribe [:get [:ui :add-image-url]])
                          :on-success #(rf/dispatch [::add-and-select-image
                                                     promotion-index
                                                     {:id (:pk %)
                                                      :url (:normal_thumb_url %)
                                                      :source (keyword (:source %))
                                                      :categories []
                                                      :thumbnail-url (:library_thumb_url %)}])}))

(defn- image-section-expanded? [expanded current-category section-id]
  (cond
    (contains? expanded section-id) (expanded section-id)
    (some? current-category) (= section-id :current-category)
    :else (= section-id :partner-images)))

(rf/reg-sub ::image-section-expanded?
  :<- [:get [:ui :image-section-expanded]]
  :<- [:get [:product :what :category]]

  (fn [[expanded current-category] [_ section-id]]
    (image-section-expanded? expanded current-category section-id)))

(rf/reg-event-db ::toggle-section-expanded
  (fn [db [_ section-id]]
    (let [expanded (get-in db [:ui :image-section-expanded])
          current-category (get-in db [:product :what :category])
          expanded? (image-section-expanded? expanded current-category section-id)]
      (assoc-in db [:ui :image-section-expanded section-id] (not expanded?)))))

(defn- image-library-image [promotion-index & {:keys [is-current? section-id machtfit-admin?]
                                               {:keys [id thumbnail-url usages]} :image}]
  (let [item-id (str section-id "-" id)]
    ^{:key item-id}
    [:div.image
     {:class (when is-current? "current")
      :on-click #(rf/dispatch [::select-image promotion-index id])}
     (when machtfit-admin?
       [:p (image-usage-text usages)])
     [:img
      {:src thumbnail-url
       :style thumbnail-size
       :id item-id}]]))

(defn- image-gallery [promotion-index image-id section-id category]
  [:div.image-gallery
   (let [machtfit-admin? @(rf/subscribe [::product-form/machtfit-admin?])
         images (if (some? category)
                  @(rf/subscribe [::images-in-category category])
                  @(rf/subscribe [::partner-images]))]
     (doall
      (for [image' (sort (compare-by :source :desc :id :asc) images)]
        (image-library-image promotion-index :image image'
                             :is-current? (= (:id image') image-id)
                             :section-id section-id
                             :machtfit-admin? machtfit-admin?))))])

(defn- image-library-section [promotion-index image-id & {:keys [section-id category]}]
  (let [expanded? @(rf/subscribe [::image-section-expanded? section-id])
        images-in-category? (if (some? category)
                              @(rf/subscribe [::images-in-category? category])
                              @(rf/subscribe [::partner-images?]))
        title' (or category "Meine Bilder")]
    (when images-in-category?
      [:div
       {:style {:clear "left"}}
       [:h2
        {:on-click #(rf/dispatch [::toggle-section-expanded section-id])
         :style {:cursor "pointer" :white-space "nowrap"}}
        [:i.material-icons
         {:style {:line-height "21px" :vertical-align "middle"}}
         (if expanded? "keyboard_arrow_down" "keyboard_arrow_right")]
        [:span {:style {:vertical-align "middle"}} title']]
       (when expanded?
         [image-gallery promotion-index image-id section-id category])])))

(defn image-library [promotion-index image-id]
  (let [;; The category currently selected in the form.
        current-category @(rf/subscribe [:get [:product :what :category]])
        image-categories @(rf/subscribe [::image-categories])

        ;; Only those image categories that the partner has products in - these are shown earlier.
        partner-product-categories (disj @(rf/subscribe [:get [:partner-product-categories]]) current-category)

        ;; The rest of the categories - these are shown later
        other-categories (disj (set/difference image-categories partner-product-categories) current-category)

        has-partner-product-images? (some (fn [category]
                                            @(rf/subscribe [::images-in-category? category])) partner-product-categories)

        has-other-images? (some (fn [category]
                                  @(rf/subscribe [::images-in-category? category])) other-categories)]
    [:div#image-library-container
     {:style {:position "fixed"
              :top "5%"
              :max-height "80%"
              :overflow-y "auto"}}
     [:div.image-library-content.card
      {:style {:border-radius "0.5rem"
               :margin-top "0.3rem"
               :margin-bottom "0.3rem"}}
      [:div.right>a.waves-effect.waves-light.btn.btn-flat
       {:on-click #(rf/dispatch [::toggle-promotion-image-library-active nil])}
       [:i.material-icons.left "cancel"]
       "Schließen"]
      [:div#image-library
       {:style {:clear "right"}}
       (list
        ^{:key "add-image"}
        [image-library-upload promotion-index]

        ^{:key "current-image"}
        [:div.col.s4
         {:style {:float "right" :margin-top "4rem"}}
         [image promotion-index {:image-id image-id} false]]

        (when (some? current-category)
          (let [section-id :current-category]
            ^{:key section-id}
            [image-library-section promotion-index image-id :section-id section-id :category current-category]))

        (let [section-id :partner-images]
          ^{:key section-id}
          [image-library-section promotion-index image-id :section-id section-id])

        (when has-partner-product-images?
          ^{:key "partner-product-categories"}
          [:div
           {:style {:clear "left"}}
           [:h3
            {:style {:white-space "nowrap" :margin-top "2rem"}}
            "Kategorien Ihrer Angebote"]
           (doall
            (for [category partner-product-categories]
              (let [section-id (keyword (str "category-" category))]
                ^{:key section-id}
                [image-library-section promotion-index image-id :section-id section-id :category category])))])

        (when has-other-images?
          ^{:key "other-categories"}
          [:div
           {:style {:clear "left"}}
           [:h3
            {:style {:white-space "nowrap" :margin-top "2rem"}}
            "Weitere Kategorien"]
           (doall
            (for [category other-categories]
              (let [section-id (keyword (str "category-" category))]
                ^{:key section-id}
                [image-library-section promotion-index image-id :section-id section-id :category category])))]))]]]))
